import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faGraduationCap, 
    faUserCheck, 
    faExclamationTriangle,
    faTasks,
    faCertificate,
    faHardHat,
    faChartLine,
    faCalendar,
    faBell,
    faUsers,
    faWarning,
    faCheckCircle,
    faPlus
} from '@fortawesome/free-solid-svg-icons';
import './StaffSafetyDashboard.css';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';

// Static data structure matching Roof Safety Dashboard visuals, with staff data
const stats = [
  {
    key: 'totalStaff',
    label: 'Total Staff',
    icon: faUsers,
    total: 45,
    failed: 0,
    status: 'good',
  },
  {
    key: 'activeTrainings',
    label: 'Active Trainings',
    icon: faGraduationCap,
    total: 12,
    failed: 0,
    status: 'good',
    navTarget: 'training',
  },
  {
    key: 'pendingInductions',
    label: 'Pending Inductions',
    icon: faUserCheck,
    total: 5,
    failed: 5,
    status: 'warning',
    navTarget: 'inductions',
  },
  {
    key: 'recentIncidents',
    label: 'Recent Incidents',
    icon: faExclamationTriangle,
    total: 3,
    failed: 3,
    status: 'alert',
    navTarget: 'incidents',
  },
  {
    key: 'overdueTasks',
    label: 'Overdue Tasks',
    icon: faTasks,
    total: 8,
    failed: 8,
    status: 'alert',
    navTarget: 'tasks',
  },
  {
    key: 'expiringCertifications',
    label: 'Expiring Certifications',
    icon: faCertificate,
    total: 7,
    failed: 7,
    status: 'warning',
    navTarget: 'certifications',
  },
  {
    key: 'ppeIssued',
    label: 'PPE Issued',
    icon: faHardHat,
    total: 38,
    failed: 0,
    status: 'good',
    navTarget: 'ppe',
  },
];

// Recent Activity Card Component
const RecentActivityCard = ({ onViewAllClick }) => {
  const navigate = useNavigate();
  const { id: propertyId } = useParams();
  const recentActivity = [
    {
      message: "3 staff inducted this week",
      timestamp: "26 Oct, 14:32",
      type: "Induction",
      status: "PASSED"
    },
    {
      message: "First aid session scheduled",
      timestamp: "26 Oct, 09:10",
      type: "Training",
      status: "TASK"
    },
    {
      message: "Incident INC-24 resolved",
      timestamp: "25 Oct, 16:48",
      type: "Incident",
      status: "PASSED"
    },
    {
      message: "4 certifications expiring within 30 days",
      timestamp: "25 Oct, 11:05",
      type: "Certifications",
      status: "TASK"
    },
    {
      message: "ST-15 training completed",
      timestamp: "25 Oct, 08:30",
      type: "Training",
      status: "PASSED"
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "FAILED":
        return <FontAwesomeIcon icon={faWarning} style={{ color: '#dc2626', fontSize: '12px' }} />;
      case "PASSED":
        return <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981', fontSize: '12px' }} />;
      case "ADDED":
        return <FontAwesomeIcon icon={faPlus} style={{ color: '#3b82f6', fontSize: '12px' }} />;
      case "TASK":
        return <FontAwesomeIcon icon={faTasks} style={{ color: '#64748b', fontSize: '12px' }} />;
      default:
        return <FontAwesomeIcon icon={faTasks} style={{ color: '#64748b', fontSize: '12px' }} />;
    }
  };

  return (
    <div className="dashboard-panel activity-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div className="panel-title" style={{ marginBottom: 0 }}>
          <FontAwesomeIcon icon={faBell} /> Recent Activity
        </div>
        <button 
          style={{ color: '#3b82f6', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={onViewAllClick || (() => navigate(`/propertydashboard/${propertyId}/activity`))}
        >
          View All
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, overflow: 'hidden' }}>
        {recentActivity.map((activity, index) => (
          <div key={index} style={{ padding: '6px 0', backgroundColor: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flexShrink: 0 }}>
                {getStatusIcon(activity.status)}
              </div>
              <div style={{ flex: 1, minWidth: 0, backgroundColor: '#fff', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', color: '#0f172a', lineHeight: '1.3', fontWeight: '500', backgroundColor: '#fff', textAlign: 'left' }}>{activity.message}</div>
                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', backgroundColor: '#fff', textAlign: 'left' }}>{activity.timestamp}</div>
              </div>
              <div style={{ flexShrink: 0 }}>
                <span style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  padding: '2px 6px', 
                  borderRadius: '4px', 
                  fontSize: '9px', 
                  backgroundColor: '#f1f5f9', 
                  color: '#64748b',
                  fontWeight: '500'
                }}>
                  {activity.type}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StaffSafetyDashboard = ({ propertyId, onNavigate }) => {
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');
  const attentionItems = stats.filter(item => item.failed > 0);

  return (
    <div className="staff-dashboard-wrapper">
      <div className="dashboard-header-row">
        <h2 className="dashboard-title">Staff Safety Dashboard</h2>
        <div className="dashboard-actions">
          <button className="btn btn-primary" onClick={() => { setComingSoonFeature('View Alerts'); setIsComingSoonOpen(true); }}>
            <FontAwesomeIcon icon={faBell} /> View Alerts
          </button>
          <button className="btn btn-primary" onClick={() => { setComingSoonFeature('Generate Report'); setIsComingSoonOpen(true); }}>
            <FontAwesomeIcon icon={faCalendar} /> Generate Report
          </button>
        </div>
      </div>
      <div className="dashboard-cards-grid">
        {stats.map((item) => (
          <div key={item.key} className={`dashboard-card`} onClick={() => onNavigate && item.navTarget && onNavigate(item.navTarget)} role={item.navTarget ? 'button' : undefined} tabIndex={item.navTarget ? 0 : undefined}>
            <div className="dashboard-card-icon">
              <FontAwesomeIcon icon={item.icon} />
            </div>
            <div className="dashboard-card-info">
              <div className="dashboard-card-label">{item.label}</div>
              <div className="dashboard-card-numbers">
                <span className="dashboard-card-total">{item.total}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="dashboard-bottom-panels">
        <div className="dashboard-panel attention-panel">
          <div className="panel-title">
            <FontAwesomeIcon icon={faExclamationTriangle} /> Required Attention
          </div>
          {attentionItems.length > 0 ? (
            <ul className="attention-list">
              {attentionItems.map(item => (
                <li key={item.key}>
                  <FontAwesomeIcon icon={item.icon} className="attention-icon" />
                  <span className="attention-label">{item.label}:</span>
                  <span className="attention-failed">{item.failed} Issues</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="no-issues">All systems are operational. No issues require attention.</div>
          )}
        </div>
        <div className="dashboard-panel next-inspection-panel">
          <div className="panel-title">
            <FontAwesomeIcon icon={faCalendar} /> Next Training Session
          </div>
          <div className="next-inspection-details">
            <span className="next-inspection-date">Next scheduled training: <b>March 20, 2024</b></span>
            <button className="btn btn-primary btn-small">
              <FontAwesomeIcon icon={faCalendar} /> Schedule Training
            </button>
          </div>
        </div>
        <RecentActivityCard onViewAllClick={() => { setComingSoonFeature('Activity History'); setIsComingSoonOpen(true); }} />
      </div>
      <ComingSoonModal
        isOpen={isComingSoonOpen}
        onClose={() => setIsComingSoonOpen(false)}
        featureName={comingSoonFeature}
      />
    </div>
  );
};

export default StaffSafetyDashboard;